import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import { formatCurrency } from "./constants";

// Small tinted icon chip used in section headers instead of a flat grey icon.
export function IconBadge({ icon: Icon, color = "primary", size = 30 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
      }}
    >
      <Icon sx={{ fontSize: size * 0.55 }} />
    </Box>
  );
}

// Slim ratio bar shared by traffic sources / location / category breakdowns - a gradient fill
// reads more alive than a flat block, and the width transition animates in as data loads.
function RatioBar({ percent, color = "secondary" }) {
  return (
    <Box sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", overflow: "hidden" }}>
      <Box
        sx={{
          height: "100%",
          width: `${percent}%`,
          borderRadius: 3,
          background: (theme) => `linear-gradient(90deg, ${theme.palette[color].light}, ${theme.palette[color].main})`,
          transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
    </Box>
  );
}

const RANK_ACCENTS = ["#eab308", "#94a3b8", "#c2703d"];

// Small numbered badge for ranked lists (top stores / top products), gold-silver-bronze for the
// top 3 so leaderboards read at a glance instead of blending into the list.
function RankBadge({ rank }) {
  if (!rank) return null;
  const topThree = rank <= 3;
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 800,
        color: topThree ? "#fff" : "text.secondary",
        bgcolor: topThree ? RANK_ACCENTS[rank - 1] : "action.hover",
      }}
    >
      {rank}
    </Box>
  );
}

// Mirrors Helpers/PeriodRangeHelper.cs on the backend.
export const PERIOD_OPTIONS = [
  { value: "currentMonth", label: "Current month" },
  { value: "pastMonth", label: "Past month" },
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "custom", label: "Custom" },
];

export function PeriodSelector({ period, onPeriodChange, customStart, customEnd, onCustomChange }) {
  return (
    <Stack spacing={1.5}>
      <Tabs
        value={period}
        onChange={(e, v) => onPeriodChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 600 } }}
      >
        {PERIOD_OPTIONS.map((opt) => (
          <Tab key={opt.value} value={opt.value} label={opt.label} />
        ))}
      </Tabs>
      {period === "custom" && (
        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Start date"
            type="date"
            size="small"
            value={customStart}
            onChange={(e) => onCustomChange(e.target.value, customEnd)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={customEnd}
            onChange={(e) => onCustomChange(customStart, e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      )}
    </Stack>
  );
}

// changePercent follows the backend convention: null means "new" (zero baseline, current > 0),
// 0 means genuinely flat.
export function DeltaChip({ changePercent }) {
  if (changePercent === null || changePercent === undefined) {
    return (
      <Chip
        size="small"
        icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />}
        label="New"
        color="info"
        variant="outlined"
        sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
      />
    );
  }
  if (changePercent === 0) return null;

  const isUp = changePercent > 0;
  return (
    <Tooltip title="vs. previous period">
      <Chip
        size="small"
        icon={isUp ? <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />}
        label={`${Math.abs(changePercent)}%`}
        color={isUp ? "success" : "error"}
        variant="outlined"
        sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
      />
    </Tooltip>
  );
}

export function StatCard({ icon: Icon, label, value, changePercent, helper, color = "primary", onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        position: "relative",
        height: "100%",
        p: 2.5,
        pt: 3,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: onClick ? "translateY(-3px)" : "translateY(-1px)",
          boxShadow: (theme) => `0 14px 28px ${alpha(theme.palette[color].main, 0.2)}`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: (theme) => `linear-gradient(90deg, ${theme.palette[color].light}, ${theme.palette[color].dark})`,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
          >
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800, letterSpacing: "-0.02em" }} noWrap>
            {value}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
            {changePercent !== undefined && <DeltaChip changePercent={changePercent} />}
            {helper && (
              <Typography variant="caption" color="text.secondary">
                {helper}
              </Typography>
            )}
          </Stack>
        </Box>
        <Avatar
          variant="rounded"
          sx={{
            background: (theme) => `linear-gradient(135deg, ${theme.palette[color].light}, ${theme.palette[color].dark})`,
            color: "#fff",
            width: 44,
            height: 44,
            borderRadius: 2.5,
            flexShrink: 0,
            boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette[color].main, 0.38)}`,
          }}
        >
          <Icon fontSize="small" />
        </Avatar>
      </Stack>
    </Card>
  );
}

export function SectionCard({ title, subtitle, action, children }) {
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 0.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
      {children}
    </Card>
  );
}

export function ProductRow({ product, metricLabel, metricValue, rank }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
      <RankBadge rank={rank} />
      <Avatar src={product.imageUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
        <Inventory2RoundedIcon fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {product.name || "Unnamed product"}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {metricLabel}
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
        {metricValue}
      </Typography>
    </Stack>
  );
}

const TOP_PRODUCTS_METRICS = [
  {
    value: "sold",
    label: "Sold",
    getList: (d) => d?.sold,
    metricLabel: (p) => formatCurrency(p.revenue),
    metricValue: (p) => `${formatCount(p.quantitySold)} sold`,
  },
  {
    value: "watched",
    label: "Watched",
    getList: (d) => d?.watched,
    metricLabel: () => "Product views",
    metricValue: (p) => `${formatCount(p.viewCount)} views`,
  },
  {
    value: "addedToCart",
    label: "Added to cart",
    getList: (d) => d?.addedToCart,
    metricLabel: () => "Times added to cart",
    metricValue: (p) => formatCount(p.addToCartCount),
  },
  {
    value: "wishlisted",
    label: "Wishlisted",
    getList: (d) => d?.wishlisted,
    metricLabel: () => "Times added to wishlist",
    metricValue: (p) => formatCount(p.wishlistCount),
  },
];

// Renders a SectionCard with a toggle between TopProductsSectionDto-shaped lists (sold / watched /
// added to cart / wishlisted), ranked by that metric. Pass `metrics` (a subset of those four keys)
// to restrict which toggle options are offered, e.g. for a DTO that only populates some of them.
export function TopProductsCard({ data, title = "Top products", subtitle = "Top 20 by metric, this period", metrics }) {
  const availableMetrics = metrics ? TOP_PRODUCTS_METRICS.filter((m) => metrics.includes(m.value)) : TOP_PRODUCTS_METRICS;
  const [metric, setMetric] = useState(availableMetrics[0]?.value);
  const active = availableMetrics.find((m) => m.value === metric) ?? availableMetrics[0];
  const items = active ? active.getList(data) ?? [] : [];

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      action={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(e, v) => v && setMetric(v)}
          sx={{ flexWrap: "wrap" }}
        >
          {availableMetrics.map((m) => (
            <ToggleButton key={m.value} value={m.value} sx={{ textTransform: "none", px: 1.5 }}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      {items.length > 0 ? (
        <Stack
          divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}
          sx={{ maxHeight: 480, overflowY: "auto" }}
        >
          {items.map((p, i) => (
            <ProductRow
              key={p.productId}
              product={p}
              metricLabel={active.metricLabel(p)}
              metricValue={active.metricValue(p)}
              rank={i + 1}
            />
          ))}
        </Stack>
      ) : (
        <EmptyState text="No data for this metric in this period yet." />
      )}
    </SectionCard>
  );
}

export function EmptyState({ text, icon: Icon = QueryStatsRoundedIcon }) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 4, color: "text.secondary" }}>
      <Icon sx={{ fontSize: 26, opacity: 0.35 }} />
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {text}
      </Typography>
    </Stack>
  );
}

export function formatCount(value) {
  return Number(value ?? 0).toLocaleString();
}

export function formatDayLabel(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const SOURCE_LABELS = {
  home: "Home",
  search: "Search",
  category: "Category",
  store_page: "Store page",
  product_page: "Product page",
  advertisement: "Advertisement",
  wishlist: "Wishlist",
  followed_stores: "Followed stores",
  notification_bell: "Notification bell",
  chat: "Chat",
  store_browsing: "Store browsing",
  other: "Other",
};

export function sourceLabel(source) {
  return SOURCE_LABELS[source] || source;
}

// Ranked bar list for city/district visitor breakdowns (see VisitorLocationDto on the backend).
// Coverage is partial by design: city is inferred from each logged-in visitor's default saved
// address, so guests and users with no saved address aren't attributable.
export function LocationBars({ items, getLabel, getSublabel, onItemClick }) {
  if (!items || items.length === 0) return <EmptyState text="No attributable visitor locations in this period yet." />;
  return (
    <Stack spacing={1.25}>
      {items.map((item, i) => (
        <Box
          key={`${getLabel(item)}-${i}`}
          onClick={onItemClick ? () => onItemClick(item) : undefined}
          sx={onItemClick ? { cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "rgba(15,23,42,0.03)" } } : undefined}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {getLabel(item)}
              </Typography>
              {getSublabel && getSublabel(item) && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {getSublabel(item)}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, pl: 1 }}>
              {formatCount(item.visitors)} ({item.percent}%)
            </Typography>
          </Stack>
          <RatioBar percent={item.percent} />
        </Box>
      ))}
    </Stack>
  );
}

const GPS_MIN_RADIUS = 6;
const GPS_MAX_RADIUS = 22;

function gpsMarkerRadius(count, maxCount) {
  if (maxCount <= 1) return GPS_MIN_RADIUS;
  return GPS_MIN_RADIUS + (GPS_MAX_RADIUS - GPS_MIN_RADIUS) * Math.sqrt(count / maxCount);
}

// Rough bounding box for Israel, used to bias the map's initial view toward the home market
// instead of zooming out to fit every stray fix worldwide (e.g. a visitor abroad).
const ISRAEL_BOUNDS = { minLat: 29.4, maxLat: 33.4, minLon: 34.2, maxLon: 35.9 };

function isInIsrael(p) {
  return (
    p.latitude >= ISRAEL_BOUNDS.minLat &&
    p.latitude <= ISRAEL_BOUNDS.maxLat &&
    p.longitude >= ISRAEL_BOUNDS.minLon &&
    p.longitude <= ISRAEL_BOUNDS.maxLon
  );
}

// Map of raw device GPS fixes captured on app open (see VisitorGpsPointDto on the backend). Unlike
// LocationBars this needs no saved address - it's the client's actual device location, so it also
// covers guests - but only visitors who granted location permission are represented at all. Points
// are pre-clustered server-side to ~110m so nearby fixes collapse into one pin.
export function VisitorGpsMap({ points }) {
  if (!points || points.length === 0) return <EmptyState text="No GPS fixes recorded in this period yet." />;

  const maxCount = Math.max(...points.map((p) => p.visitors));
  // Fit the initial view to the Israel cluster when there is one, so the map opens zoomed in on
  // the home market rather than zoomed out to include far-flung outliers; falls back to fitting
  // every point when nothing falls inside Israel. Visitors abroad are still shown - just reachable
  // by scrolling/panning out instead of dictating the default zoom.
  const israelPoints = points.filter(isInIsrael);
  const boundsSource = israelPoints.length > 0 ? israelPoints : points;
  const bounds = boundsSource.map((p) => [p.latitude, p.longitude]);

  return (
    <Box sx={{ height: 360, borderRadius: 2, overflow: "hidden" }}>
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [24, 24], maxZoom: 12 }}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((p, i) => (
          <CircleMarker
            key={`${p.latitude}-${p.longitude}-${i}`}
            center={[p.latitude, p.longitude]}
            radius={gpsMarkerRadius(p.visitors, maxCount)}
            pathOptions={{ color: "#12B886", fillColor: "#12B886", fillOpacity: 0.45, weight: 1 }}
          >
            <LeafletTooltip direction="top">
              {formatCount(p.visitors)} visitor{p.visitors === 1 ? "" : "s"}
            </LeafletTooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );
}

const CATEGORY_LEVELS = [
  { value: "level1", label: "Level 1", getList: (d) => d?.level1 },
  { value: "level2", label: "Level 2", getList: (d) => d?.level2 },
  { value: "level3", label: "Level 3", getList: (d) => d?.level3 },
];

// Ranked bar list for category views (CategoryVisit events - fired whenever a client opens a
// category's product list, the closest signal this app has to a category "click"), toggled by
// taxonomy level so level-1 top categories, level-2 subcategories, and level-3 sub-subcategories
// can each be ranked independently instead of competing against each other's traffic volume.
export function CategoryPerformanceCard({ data, onCategoryClick }) {
  const [level, setLevel] = useState("level1");
  const active = CATEGORY_LEVELS.find((l) => l.value === level);
  const items = active.getList(data) ?? [];

  return (
    <SectionCard
      title="Category performance"
      subtitle="Views (opens/clicks) per category, this period"
      action={
        <ToggleButtonGroup size="small" exclusive value={level} onChange={(e, v) => v && setLevel(v)}>
          {CATEGORY_LEVELS.map((l) => (
            <ToggleButton key={l.value} value={l.value} sx={{ textTransform: "none", px: 1.5 }}>
              {l.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      {items.length > 0 ? (
        <Stack spacing={1.25} sx={{ maxHeight: 420, overflowY: "auto" }}>
          {items.map((c) => (
            <Box
              key={c.categoryId}
              onClick={onCategoryClick ? () => onCategoryClick(c) : undefined}
              sx={onCategoryClick ? { cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "rgba(15,23,42,0.03)" } } : undefined}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {c.name}
                  </Typography>
                  {c.parentName && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {c.parentName}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, pl: 1 }}>
                  {formatCount(c.views)} ({c.percent}%)
                </Typography>
              </Stack>
              <RatioBar percent={c.percent} color="info" />
            </Box>
          ))}
        </Stack>
      ) : (
        <EmptyState text="No category views recorded for this level in this period yet." />
      )}
    </SectionCard>
  );
}

// Ranked row for PlatformStoreMetricDto-shaped entries (see LocationTopStoresDto on the backend).
export function StoreRankRow({ store, metricValue, onClick, rank }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      onClick={onClick}
      sx={{
        py: 1,
        px: 1,
        borderRadius: 1.5,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { bgcolor: "rgba(15,23,42,0.03)" } : undefined,
      }}
    >
      <RankBadge rank={rank} />
      <Avatar src={store.logoUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
        <StorefrontRoundedIcon fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {store.storeName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {store.rating > 0 ? `${store.rating.toFixed(1)} ★ (${store.reviewCount})` : "No ratings yet"}
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
        {metricValue}
      </Typography>
    </Stack>
  );
}

const LOCATION_TOP_STORES_METRICS = [
  { value: "bySales", label: "By sales", getList: (d) => d?.bySales, metricValue: (s) => formatCurrency(s.revenue) },
  { value: "byVisits", label: "By visits", getList: (d) => d?.byVisits, metricValue: (s) => `${formatCount(s.visits)} visits` },
];

// Toggle between LocationTopStoresDto.bySales / byVisits (a city or district's most popular
// stores, attributed the same way as the section's other metrics - see CityAnalyticsDto /
// DistrictAnalyticsDto on the backend).
export function LocationTopStoresCard({ data, title = "Top stores", subtitle, onStoreClick }) {
  const [metric, setMetric] = useState("bySales");
  const active = LOCATION_TOP_STORES_METRICS.find((m) => m.value === metric);
  const items = active.getList(data) ?? [];

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      action={
        <ToggleButtonGroup size="small" exclusive value={metric} onChange={(e, v) => v && setMetric(v)}>
          {LOCATION_TOP_STORES_METRICS.map((m) => (
            <ToggleButton key={m.value} value={m.value} sx={{ textTransform: "none", px: 1.5 }}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      {items.length > 0 ? (
        <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
          {items.map((s, i) => (
            <StoreRankRow
              key={s.storeId}
              store={s}
              metricValue={active.metricValue(s)}
              onClick={onStoreClick ? () => onStoreClick(s) : undefined}
              rank={i + 1}
            />
          ))}
        </Stack>
      ) : (
        <EmptyState text="No store activity recorded for this metric in this period yet." />
      )}
    </SectionCard>
  );
}

export function TrafficSourceBars({ sources }) {
  if (!sources || sources.length === 0) return <EmptyState text="No traffic recorded in this period yet." />;
  return (
    <Stack spacing={1.25}>
      {sources.map((s) => (
        <Box key={s.source}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">{sourceLabel(s.source)}</Typography>
            <Typography variant="body2" fontWeight={700}>
              {s.percent}%
            </Typography>
          </Stack>
          <RatioBar percent={s.percent} />
        </Box>
      ))}
    </Stack>
  );
}
